import SuperTokensLock from 'browser-tabs-lock';
import hardtack from 'hardtack';
import { ACC_COOKIE_KEY, REF_COOKIE_KEY } from './jwt';
import { logoutAndRedirect } from './logoutAndRedirect';
import { getAccessTokenWithRef } from '../domains/authentication/api/api';

const LOCK_KEY = 'REF_TOKEN_LOCK_KEY';
const TOKEN_EXP_SEC = 60;

const superTokensLock = new SuperTokensLock();

type CheckAndReloadAccessToken = () => Promise<string | undefined>;

const retryPromise = async (
  cb: () => Promise<boolean>,
  maxNumberOfRetries = 3,
): Promise<boolean> => {
  for (let i = 0; i < maxNumberOfRetries; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    if (await cb()) {
      return true;
    }
  }

  return false;
};

const isAccValid = () => {
  const acc = hardtack.get(ACC_COOKIE_KEY);
  const [, tokenBody] = acc?.split('.') ?? [];
  if (tokenBody) {
    try {
      const parsedTokenBody = JSON.parse(atob(tokenBody)) as { exp?: number };
      const nowAsSecond = Date.now() / 1000;
      return parsedTokenBody.exp && parsedTokenBody.exp - TOKEN_EXP_SEC > nowAsSecond;
    } catch {
      return false;
    }
  }
  return false;
};

const shouldRefreshAccessToken = () => {
  const ref = hardtack.get(REF_COOKIE_KEY);

  /**
   * If acc is not existed, it means it is expired, and it should be refreshed with refresh token.
   */
  return !isAccValid() && !!ref;
};

const tryRenewingAccessToken = async (resolve: (token?: string) => void) => {
  let token: string | undefined;
  if (!shouldRefreshAccessToken()) {
    resolve();
    return;
  }

  const release = () => {
    superTokensLock.releaseLock(LOCK_KEY).finally(() => {
      resolve(token);
    });
  };

  if (await retryPromise(() => superTokensLock.acquireLock(LOCK_KEY, 5000), 10)) {
    try {
      const ref = hardtack.get(REF_COOKIE_KEY);
      if (shouldRefreshAccessToken() && ref) {
        /**
         * shouldRefreshAccessToken: Can we still refresh the access token?
         * ref: It is just a null check.
         */
        token = await getAccessTokenWithRef(ref);
      }
    } catch {
      await logoutAndRedirect();
    } finally {
      release();
    }
  } else {
    release();
  }
};

export const checkAndReloadAccessTokenWrapperFunc = (): CheckAndReloadAccessToken => {
  return () =>
    new Promise<string | undefined>((resolve) => {
      tryRenewingAccessToken(resolve);
    });
};

export const checkAndReloadAccessToken: CheckAndReloadAccessToken =
  checkAndReloadAccessTokenWrapperFunc();
